import { Component, DestroyRef, inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, forkJoin, of } from 'rxjs';
import { DatabaseOverview, OrderListItem } from 'reg-hub-common';
import { OrderListRepositoryService } from '../../../services/order-list/order-list-repository.service';
import { DatabaseOverviewService } from '../../../services/database-overview/database-overview.service';
import { MatTableDataSource } from '@angular/material/table';
import { OrdersHubService } from '../../../services/hubs/orders-hub/orders-hub.service';
import { MatPaginator } from '@angular/material/paginator';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DashboardComponent implements OnDestroy {

  protected _loading: boolean = false;

  protected _failedOrders: OrderListItem[] = [];
  protected _totalFailedOrders: number = 0;

  protected _manualOrders: OrderListItem[] = [];
  protected _totalManualOrders: number = 0;

  protected _databaseOverviews!: MatTableDataSource<DatabaseOverview, MatPaginator>;

  private destroyRef = inject(DestroyRef);

  constructor(
    private ordersRepo: OrderListRepositoryService,
    private databaseOverviewRepo: DatabaseOverviewService,
    private ordersHubService: OrdersHubService
  ) { }

  ngOnInit(): void {
    this._loading = true;
    this.ordersHubService.connect();

    const failedOrdersRequest = this.ordersRepo.getTopTenFailedOrders().pipe(catchError(() => of({ total: 0, resource: [] as OrderListItem[] })));
    const manualOrdersRequest = this.ordersRepo.getTopTenManualOrders().pipe(catchError(() => of({ total: 0, resource: [] as OrderListItem[] })));
    const databaseOverviewRequest = this.databaseOverviewRepo.getDatabaseOverviews().pipe(catchError(() => of([] as DatabaseOverview[])));
    // Only requests whose results this component renders. The monitor tables
    // load their own data so the page spinner doesn't wait on them.
    forkJoin({
      failedOrders: failedOrdersRequest,
      manualOrders: manualOrdersRequest,
      databaseOverviews: databaseOverviewRequest
    }).subscribe({ 
      next: responses => {
        this._databaseOverviews = new MatTableDataSource(responses.databaseOverviews);
        this.ordersHubService.setInitialManualOrders(responses.manualOrders.resource, responses.manualOrders.total);
        this.ordersHubService.setInitialFailedOrders(responses.failedOrders.resource, responses.failedOrders.total);

        this.subscribeToOrderUpdates();
      },
      // complete never fires on error, so without this the overlay stays up forever.
      error: () => this._loading = false,
      complete: () => this._loading = false 
    });

    // register for updates from failed, manual, handled groups
    this.ordersHubService.joinDashboardGroups();
  }

  private subscribeToOrderUpdates() {
    // Subscribe to manual orders updates
    this.ordersHubService.manualOrders$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((orders) => {
      this._manualOrders = orders;
    });

    //Subscribe to manual orders count
    this.ordersHubService.manualOrdersCount$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(count => {
      this._totalManualOrders = count;
    });

    // Subscribe to failed orders updates
    this.ordersHubService.failedOrders$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((orders) => {
      this._failedOrders = orders;
    });

    //Subscribe to failed oders count
    this.ordersHubService.failedOrdersCount$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(count => {
      this._totalFailedOrders = count;
    });
  }

  ngOnDestroy() {
    // Leave manual / failed / handled order groups if dashboard is not active
    this.ordersHubService.leaveDashboardGroups();
  }
}
